package org.catharsis.spendtel.core.impl

import io.github.aakira.napier.Napier
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import org.catharsis.spendtel.core.api.AuthTokenProvider
import org.catharsis.spendtel.core.api.PaymentRequiredException
import org.catharsis.spendtel.core.api.SpendTelService
import org.catharsis.spendtel.core.network.SpendTelApiConfig
import org.catharsis.spendtel.core.network.parseApiErrorMessage
import org.catharsis.spendtel.feature.data.BillingEntitlement
import org.catharsis.spendtel.feature.data.CheckoutSessionRequest
import org.catharsis.spendtel.feature.data.CheckoutSessionResponse
import org.catharsis.spendtel.feature.data.CreateExpenseReportRequest
import org.catharsis.spendtel.feature.data.ExpenseReportResponse
import org.catharsis.spendtel.feature.data.PaymentRequiredError
import org.catharsis.spendtel.feature.data.StoragePathInput

class SpendTelServiceImpl(
    private val httpClient: HttpClient,
    private val authTokenProvider: AuthTokenProvider,
) : SpendTelService {

    override suspend fun getEntitlement(): Result<BillingEntitlement> {
        return try {
            val idToken = authTokenProvider.getIdToken()
            val response: HttpResponse = httpClient.get("${SpendTelApiConfig.BASE_URL}/billing/entitlement") {
                header(HttpHeaders.Authorization, "Bearer $idToken")
            }

            Napier.d("getEntitlement → ${response.status}")

            when (response.status) {
                HttpStatusCode.OK -> Result.success(response.body<BillingEntitlement>())
                HttpStatusCode.Unauthorized -> Result.failure(
                    Exception("Your session expired. Please sign in again."),
                )
                else -> {
                    val errorBody = runCatching { response.body<String>() }.getOrNull()
                    Result.failure(
                        Exception(
                            errorBody?.takeIf { it.isNotBlank() }
                                ?: "Could not load billing status (${response.status}).",
                        ),
                    )
                }
            }
        } catch (e: Exception) {
            Napier.e("getEntitlement failed: $e")
            Result.failure(Exception(e.message ?: "Could not load billing status.", e))
        }
    }

    override suspend fun createCheckoutSession(
        request: CheckoutSessionRequest,
    ): Result<CheckoutSessionResponse> {
        return try {
            val idToken = authTokenProvider.getIdToken()
            val response: HttpResponse = httpClient.post("${SpendTelApiConfig.BASE_URL}/billing/checkout-session") {
                contentType(ContentType.Application.Json)
                header(HttpHeaders.Authorization, "Bearer $idToken")
                setBody(request)
            }

            Napier.d("createCheckoutSession → ${response.status}")

            when (response.status) {
                HttpStatusCode.OK, HttpStatusCode.Created -> {
                    Result.success(response.body<CheckoutSessionResponse>())
                }
                HttpStatusCode.Unauthorized -> Result.failure(
                    Exception("Your session expired. Please sign in again."),
                )
                else -> {
                    val errorBody = runCatching { response.body<String>() }.getOrNull()
                    Result.failure(
                        Exception(
                            parseApiErrorMessage(
                                errorBody,
                                "Could not start checkout (${response.status}).",
                            ),
                        ),
                    )
                }
            }
        } catch (e: Exception) {
            Napier.e("createCheckoutSession failed: $e")
            Result.failure(Exception(e.message ?: "Could not start checkout.", e))
        }
    }

    override suspend fun createExpenseReport(
        storagePaths: List<StoragePathInput>,
    ): Result<ExpenseReportResponse> {
        if (storagePaths.isEmpty()) {
            return Result.failure(IllegalArgumentException("Select at least one statement to analyze."))
        }

        return try {
            val idToken = authTokenProvider.getIdToken()
            val response: HttpResponse = httpClient.post("${SpendTelApiConfig.BASE_URL}/expense-reports") {
                contentType(ContentType.Application.Json)
                header(HttpHeaders.Authorization, "Bearer $idToken")
                setBody(CreateExpenseReportRequest(storagePaths = storagePaths))
            }

            Napier.d("createExpenseReport → ${response.status}")

            when (response.status) {
                HttpStatusCode.Created -> Result.success(response.body<ExpenseReportResponse>())
                HttpStatusCode.PaymentRequired -> {
                    val error = response.body<PaymentRequiredError>()
                    Result.failure(PaymentRequiredException(error))
                }
                HttpStatusCode.Unauthorized -> Result.failure(
                    Exception("Your session expired. Please sign in again."),
                )
                HttpStatusCode.BadRequest -> Result.failure(
                    Exception(
                        "Invalid statement paths. The user ID in each path must match your signed-in account.",
                    ),
                )
                HttpStatusCode.InternalServerError -> Result.failure(
                    Exception("Server error while starting your report. Please try again shortly."),
                )
                else -> {
                    val errorBody = runCatching { response.body<String>() }.getOrNull()
                    Result.failure(
                        Exception(
                            errorBody?.takeIf { it.isNotBlank() }
                                ?: "Could not create expense report (${response.status}).",
                        ),
                    )
                }
            }
        } catch (e: Exception) {
            Napier.e("createExpenseReport failed: $e")
            val message = when {
                e.message?.contains("timeout", ignoreCase = true) == true ->
                    "Analysis is taking longer than expected. Open your report from Home to check its status."
                else -> e.message ?: "Could not create expense report."
            }
            Result.failure(Exception(message, e))
        }
    }
}
